/* R35 — Project sourcing rollup view. Used in ProjectDetail + ProjectPreview. */
import { Fragment, useState } from "react";
import { AlertTriangle } from "lucide-react";

export default function ProjectSourcingRollup({ rollup }) {
  const [openMat, setOpenMat] = useState(new Set());
  const toggleMat = (id) => setOpenMat((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (!rollup) return null;

  if (rollup.project_sourcing_rollup_unavailable) {
    return (
      <div
        className="bg-slate-50 border border-slate-200 rounded p-3 text-sm text-slate-600 mb-4"
        data-testid="project-rollup-unavailable"
      >
        Sourcing rollup unavailable — {rollup.project_sourcing_rollup_reason}
      </div>
    );
  }

  return (
    <div className="mb-6 space-y-4" data-testid="project-rollup">
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-100 px-3 py-2 font-semibold text-sm flex items-center gap-2">
          <span>Monthly RM by Material (project total)</span>
          {rollup.any_low_volume_flag && (
            <span
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 flex items-center gap-1"
              data-testid="project-rollup-flag-pill"
            >
              <AlertTriangle size={11} /> flagged
            </span>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="text-left p-2">Material</th>
              <th className="text-left p-2">Thickness (mm)</th>
              <th className="text-right p-2">Monthly kg</th>
              <th className="text-right p-2">Threshold</th>
              <th className="text-center p-2 w-16">Flag</th>
            </tr>
          </thead>
          <tbody>
            {(rollup.monthly_rm_by_material || []).map((g) => {
              const gid = `${g.material_id}||${g.material_grade}`;
              const byThk = g.by_thickness || [];
              const canExpand = byThk.length > 1;
              const isOpen = openMat.has(gid);
              return (
              <Fragment key={gid}>
              <tr
                className={`border-t ${canExpand ? "cursor-pointer hover:bg-slate-50" : ""}`}
                onClick={canExpand ? () => toggleMat(gid) : undefined}
                data-testid={`project-mat-row-${g.material_id}`}
              >
                <td className="p-2">
                  {canExpand && <span className="inline-block w-3 text-slate-400">{isOpen ? "▾" : "▸"}</span>}{" "}
                  {g.material_label}
                </td>
                <td className="p-2 text-slate-600">
                  {canExpand ? (
                    <span className="text-indigo-600 text-xs">
                      {isOpen ? "hide" : "click to split"}
                    </span>
                  ) : (
                    g.thickness_label || "—"
                  )}
                </td>
                <td className="p-2 text-right font-mono">
                  {Number(g.monthly_rm_kg).toLocaleString("en-IN", { maximumFractionDigits: 1 })} kg
                </td>
                <td className="p-2 text-right text-slate-500">
                  {g.threshold_kg != null
                    ? `${Number(g.threshold_kg).toLocaleString("en-IN", { maximumFractionDigits: 0 })} kg`
                    : "—"}
                </td>
                <td className="p-2 text-center">
                  {g.low_volume_flag ? (
                    <span className="text-red-600 font-bold" data-testid={`project-low-vol-${g.material_id}`}>⚠</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
              </tr>
              {isOpen && byThk.map((t) => (
                <tr key={gid + "|" + t.thickness_mm} className="bg-slate-50/60 border-t border-slate-100">
                  <td className="p-2"></td>
                  <td className="py-1 pl-4 pr-2 text-xs text-slate-500">{t.thickness_label} mm</td>
                  <td className="py-1 px-2 text-right font-mono text-xs text-slate-600">
                    {Number(t.monthly_rm_kg).toLocaleString("en-IN", { maximumFractionDigits: 1 })} kg
                  </td>
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                </tr>
              ))}
              </Fragment>
            );})}
            {(rollup.monthly_rm_by_material || []).length === 0 && (
              <tr>
                <td colSpan={5} className="p-3 text-center italic text-slate-500">
                  No material data yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-slate-100 px-3 py-2 font-semibold text-sm flex justify-between items-center">
          <span>Customer MOQ summary</span>
          {rollup.moq_unresolved_count > 0 && (
            <span className="text-xs text-amber-700" data-testid="project-moq-unresolved">
              {rollup.moq_unresolved_count} child{rollup.moq_unresolved_count === 1 ? "" : "ren"} missing MOQ
            </span>
          )}
        </div>
        {(!rollup.moq_summary || rollup.moq_summary.length === 0) ? (
          <p className="p-4 text-sm text-slate-500 italic" data-testid="project-moq-empty">
            No MOQ data on any child yet.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="text-left p-2">Child</th>
                <th className="text-left p-2">Type</th>
                <th className="text-right p-2">Customer MOQ</th>
                <th className="text-left p-2">Driver</th>
              </tr>
            </thead>
            <tbody>
              {rollup.moq_summary.map((row) => (
                <tr
                  key={`${row.kind}-${row.child_id}`}
                  className="border-t"
                  data-testid={`project-moq-row-${row.child_id}`}
                >
                  <td className="p-2">
                    <span className="font-mono text-xs text-slate-500">{row.child_number || "—"}</span>{" "}
                    {row.child_name || ""}
                  </td>
                  <td className="p-2 capitalize">{row.kind}</td>
                  <td className="p-2 text-right font-mono">
                    {row.is_lower_bound && <span className="text-amber-600">≥ </span>}
                    {Number(row.native_moq.value).toLocaleString("en-IN")} {row.native_moq.unit}
                  </td>
                  <td className="p-2 text-xs text-slate-500">{row.driver_note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rollup.children_skipped_unavailable_count > 0 && (
        <p
          className="text-xs text-slate-500 italic"
          data-testid="project-rollup-partial-warning"
        >
          Note: {rollup.children_skipped_unavailable_count} child
          {rollup.children_skipped_unavailable_count === 1 ? "" : "ren"} skipped (no volumes set).
        </p>
      )}
    </div>
  );
}
